import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExportButton from 'src/components/export-button';
import { PageLayout } from 'src/layouts';
import { Permission } from 'src/rbac/Permission';

import { useGetControlGroupsRegister } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { getCounter } from '@/utils/collectionUtils';

import { useGetCollectionTableProps } from './config';
import ControlGroupModal from './ControlGroupModal';
import { ControlGroupRegisterToolbar } from './ControlGroupRegisterToolbar';

const Page: FC = () => {
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'controlGroups',
  });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { data, loading, refetch } = useGetControlGroupsRegister({
    queryArgs: {},
  });

  const tableProps = useGetCollectionTableProps(data?.control_group);

  const title = st('register_title', 'Register');
  const counter = getCounter(tableProps.totalItemsCount, loading);
  // Raw (unparenthesised) count for the object toolbar — it wraps in parens
  // itself, unlike the already-parenthesised `getCounter` above (CUS-153).
  const toolbarCounter =
    loading || tableProps.totalItemsCount === undefined
      ? undefined
      : String(tableProps.totalItemsCount);

  return (
    <PageLayout
      helpTranslationKey={'controlGroups.registerHelp'}
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-153) — flag-gated; mounted only when on so
      // `useExportOptions` runs on exactly one path (toolbar vs ExportButton).
      headerOverride={
        objectToolbarEnabled ? (
          <ControlGroupRegisterToolbar
            title={title}
            counter={toolbarCounter}
            tableProps={tableProps}
            onCreate={() => setIsModalVisible(true)}
          />
        ) : undefined
      }
      actions={
        <SpaceBetween direction={'horizontal'} size={'xs'}>
          <ExportButton tableProps={tableProps} entityLabel={title} />
          <Permission permission={'insert:control_group'}>
            <Button variant={'primary'} onClick={() => setIsModalVisible(true)}>
              {st('create_new_button', 'Add')}
            </Button>
          </Permission>
        </SpaceBetween>
      }
    >
      <Table {...tableProps} loading={loading} />
      {isModalVisible && (
        <ControlGroupModal
          onDismiss={() => setIsModalVisible(false)}
          onRegisterRefetch={refetch}
        />
      )}
    </PageLayout>
  );
};

export default Page;
